import { errorFromResponse } from '@agentic/api-client';
import { api, PlatformError, TaskAttachmentResponse } from '@agentic/contracts';
import { Badge, formatNumber, type Tone } from '@agentic/ui-kit';
import { client } from '../../../app/api.ts';
import { errorParts } from '../../../app/route-states.tsx';

/** Upload state of one file chosen in the New case form. */
export type AttachmentStatus =
  | { state: 'waiting' }
  | { state: 'uploading' }
  | { state: 'uploaded'; result: TaskAttachmentResponse }
  | { state: 'failed'; error: unknown };

/** One chosen file with its form field and upload state. */
export interface AttachmentEntry {
  field: string;
  label: string;
  file: File;
  status: AttachmentStatus;
}

const BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  csv: 'text/csv',
  html: 'text/html',
  htm: 'text/html',
  json: 'application/json',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

/** The file's type, or one inferred from its extension when the browser leaves it empty (often for `.md`). */
export function attachmentContentType(file: Pick<File, 'name' | 'type'>): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[ext] ?? 'application/octet-stream';
}

/**
 * `POST /tasks/{taskId}/attachments` with the raw file as the body. The route is built by the api-client from the
 * contract; the body is sent with `fetch` because the client's JSON transport cannot carry file bytes. Errors are
 * the same `PlatformError`s the client throws.
 */
export async function uploadAttachment(
  taskId: string,
  file: File,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<TaskAttachmentResponse> {
  const url = client.url(api.tasks.attachments, { params: { taskId }, query: { filename: file.name } });
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': attachmentContentType(file), accept: 'application/json' },
      body: file,
      credentials: client.options.credentials ?? 'include',
    });
  } catch (cause) {
    throw new PlatformError('downstream_failed', 'Network error calling tasks.attachments', { cause });
  }
  if (!res.ok) throw await errorFromResponse(res, 'tasks.attachments');
  return TaskAttachmentResponse.parse(await res.json());
}

const LABELS: Record<AttachmentStatus['state'], [string, Tone]> = {
  waiting: ['Not uploaded yet', 'neutral'],
  uploading: ['Uploading', 'info'],
  uploaded: ['Uploaded', 'ok'],
  failed: ['Upload failed', 'bad'],
};

/** Per-file upload status after the case starts; failures keep the server's message visible. */
export function AttachmentStatusList({ entries }: { entries: AttachmentEntry[] }) {
  return (
    <ul aria-label="File uploads" className="flex flex-col gap-2">
      {entries.map((e) => {
        const [label, tone] = LABELS[e.status.state];
        return (
          <li
            key={e.field}
            data-upload-state={e.status.state}
            className="flex flex-col gap-1 rounded-xl border border-line px-3 py-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 break-words text-[14px] text-ink">
                <span className="text-ink-2">{e.label}: </span>
                {e.file.name}
              </span>
              <Badge tone={tone} dot pulse={e.status.state === 'uploading'}>
                {label}
              </Badge>
            </div>
            {e.status.state === 'uploaded' && (
              <span className="text-[12px] text-ink-3">
                {formatNumber(e.status.result.size)} bytes · sha256{' '}
                <code className="font-mono" title={e.status.result.sha256}>
                  {e.status.result.sha256.slice(0, 12)}…
                </code>
              </span>
            )}
            {e.status.state === 'failed' && (
              <span className="text-[13px] text-bad-ink">{errorParts(e.status.error).message}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
